// C2 마물 전투 연출 풀 4종(드로우콜 각 1): 늘어난 줄(바람 줄·투사체 꼬리·돌진 줄), 회전 빌보드(투사체 머리), 반투명 구체(보호막), 바닥 판(소환 룬·분열 균열).
// 모두 생성 때 한 번 할당하고(InstancedMesh · 평면 배열) 프레임 루프에서 객체를 만들지 않는다. 가득 차면 가장 오래된 것을 대체한다.
// 텍스처가 없다: 모양은 전부 TSL 절차형이다(새 텍스처 파일 없음, Runware 사용 없음).
// "즉시 모드"(begin/push/end)는 게임 뷰에서 매 프레임 위치를 읽어 그리는 것(투사체·보호막·균열)을 위한 칸이다 — 풀 상태를 갖지 않는다.
import * as THREE from 'three/webgpu';
import type { Node } from 'three/webgpu';
import { abs, atan, attribute, clamp, float, length, mix, normalView, output, positionViewDirection, pow, sin, smoothstep, step, uv, vec2, vec3, vec4 } from 'three/tsl';

const lineMask = (d: Node<'float'>, w: number): Node<'float'> => float(1).sub(smoothstep(0, w, abs(d)));

function baseMaterial(additive: boolean): THREE.MeshBasicNodeMaterial {
  const m = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: additive ? THREE.AdditiveBlending : THREE.NormalBlending });
  m.forceSinglePass = true;
  m.fog = false;
  return m;
}

function makeInstanced(geo: THREE.BufferGeometry, mat: THREE.Material, cap: number, renderOrder: number, name: string): { mesh: THREE.InstancedMesh; alpha: THREE.InstancedBufferAttribute } {
  const alpha = new THREE.InstancedBufferAttribute(new Float32Array(cap), 1);
  alpha.setUsage(THREE.DynamicDrawUsage);
  geo.setAttribute('aAlpha', alpha);
  const mesh = new THREE.InstancedMesh(geo, mat, cap);
  mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
  mesh.setColorAt(0, new THREE.Color(1, 1, 1));
  (mesh.instanceColor as THREE.InstancedBufferAttribute).setUsage(THREE.DynamicDrawUsage);
  mesh.frustumCulled = false;
  mesh.count = 0;
  mesh.renderOrder = renderOrder;
  mesh.name = name;
  return { mesh, alpha };
}

const _c = new THREE.Color();
const hex = (c: number): void => { _c.setHex(c); };

/**
 * 늘어난 줄: 머리 위치·방향·길이·폭을 가진 가산 띠. 카메라를 향해 폭을 펼친다(옆에서도 얇아지지 않는다).
 * 시뮬레이션 입자(바람 줄·돌진 줄: 속도로 날아가며 옅어짐) + 즉시 칸(투사체 꼬리) 두 구역이 한 메시를 쓴다.
 */
export class StretchPool {
  readonly mesh: THREE.InstancedMesh;
  private readonly alpha: THREE.InstancedBufferAttribute;
  n = 0;
  /** 이번 프레임 즉시 칸 수 */
  im = 0;
  private fx = 0; private fy = 0; private fz = -1;
  private readonly px: Float32Array; private readonly py: Float32Array; private readonly pz: Float32Array;
  private readonly vx: Float32Array; private readonly vy: Float32Array; private readonly vz: Float32Array;
  private readonly dx: Float32Array; private readonly dy: Float32Array; private readonly dz: Float32Array;
  private readonly len: Float32Array; private readonly wid: Float32Array;
  private readonly age: Float32Array; private readonly life: Float32Array;
  private readonly cr: Float32Array; private readonly cg: Float32Array; private readonly cb: Float32Array; private readonly a0: Float32Array;

  /** simCap: 시뮬레이션 칸, immCap: 즉시 칸 */
  constructor(readonly simCap: number, readonly immCap: number, renderOrder = 23) {
    const cap = simCap + immCap;
    const geo = new THREE.PlaneGeometry(1, 1);
    const mat = baseMaterial(true);
    const u = uv();
    // u: 꼬리 0 → 머리 1, v: 가로질러. 머리가 가장 밝고 꼬리는 가늘게 사라진다
    const across = abs(u.y.mul(2).sub(1));
    const taper = float(1).sub(across.mul(float(1).sub(u.x.mul(0.65))));
    const body = clamp(taper, 0, 1).pow(1.8);
    const along = pow(u.x, 1.5);
    const tip = float(1).sub(smoothstep(0.93, 1.0, u.x).mul(0.5));
    mat.colorNode = vec3(1, 1, 1).mul(float(0.9).add(pow(u.x, 5).mul(1.6)));
    mat.opacityNode = body.mul(along).mul(tip).mul(attribute('aAlpha', 'float'));
    const { mesh, alpha } = makeInstanced(geo, mat, cap, renderOrder, 'mob-fx-stretch');
    this.mesh = mesh; this.alpha = alpha;
    const f = (): Float32Array => new Float32Array(simCap);
    this.px = f(); this.py = f(); this.pz = f(); this.vx = f(); this.vy = f(); this.vz = f();
    this.dx = f(); this.dy = f(); this.dz = f(); this.len = f(); this.wid = f();
    this.age = f(); this.life = f(); this.cr = f(); this.cg = f(); this.cb = f(); this.a0 = f();
  }

  setView(fx: number, fy: number, fz: number): void { this.fx = fx; this.fy = fy; this.fz = fz; }

  /** 머리 위치 (x,y,z), 방향(단위 벡터 d), 속도 v. 가득 차면 가장 오래된 것을 대체한다. 대체했으면 true */
  spawn(x: number, y: number, z: number, dx: number, dy: number, dz: number, vx: number, vy: number, vz: number, len: number, wid: number, life: number, color: number, alpha: number, cap = this.simCap): boolean {
    let replaced = false;
    if (this.n >= Math.min(cap, this.simCap)) { this.evictOldest(); replaced = true; }
    const i = this.n++;
    this.px[i] = x; this.py[i] = y; this.pz[i] = z; this.dx[i] = dx; this.dy[i] = dy; this.dz[i] = dz;
    this.vx[i] = vx; this.vy[i] = vy; this.vz[i] = vz; this.len[i] = len; this.wid[i] = wid;
    this.age[i] = 0; this.life[i] = life; this.a0[i] = alpha;
    hex(color); this.cr[i] = _c.r; this.cg[i] = _c.g; this.cb[i] = _c.b;
    return replaced;
  }

  private kill(i: number): void {
    const j = --this.n;
    if (i === j) return;
    this.px[i] = this.px[j]; this.py[i] = this.py[j]; this.pz[i] = this.pz[j]; this.vx[i] = this.vx[j]; this.vy[i] = this.vy[j]; this.vz[i] = this.vz[j];
    this.dx[i] = this.dx[j]; this.dy[i] = this.dy[j]; this.dz[i] = this.dz[j]; this.len[i] = this.len[j]; this.wid[i] = this.wid[j];
    this.age[i] = this.age[j]; this.life[i] = this.life[j]; this.cr[i] = this.cr[j]; this.cg[i] = this.cg[j]; this.cb[i] = this.cb[j]; this.a0[i] = this.a0[j];
  }

  evictOldest(): void {
    if (this.n === 0) return;
    let best = 0, bf = -1;
    for (let i = 0; i < this.n; i++) { const f = this.age[i] / this.life[i]; if (f > bf) { bf = f; best = i; } }
    this.kill(best);
  }

  private put(i: number, hx: number, hy: number, hz: number, dx: number, dy: number, dz: number, len: number, wid: number, r: number, g: number, b: number, a: number): void {
    // 폭 방향 = 시선 × 선 방향(선이 시선과 거의 나란하면 위쪽)
    let qx = this.fy * dz - this.fz * dy, qy = this.fz * dx - this.fx * dz, qz = this.fx * dy - this.fy * dx;
    const l = Math.hypot(qx, qy, qz);
    if (l < 1e-3) { qx = 0; qy = 1; qz = 0; } else { qx /= l; qy /= l; qz /= l; }
    const m = this.mesh.instanceMatrix.array as Float32Array;
    const o = i * 16;
    m[o] = dx * len; m[o + 1] = dy * len; m[o + 2] = dz * len; m[o + 3] = 0;
    m[o + 4] = qx * wid; m[o + 5] = qy * wid; m[o + 6] = qz * wid; m[o + 7] = 0;
    m[o + 8] = this.fx; m[o + 9] = this.fy; m[o + 10] = this.fz; m[o + 11] = 0;
    // 평면이 머리를 +x 끝으로 두도록 중심 = 머리 − 방향 × 길이 / 2
    m[o + 12] = hx - dx * len * 0.5; m[o + 13] = hy - dy * len * 0.5; m[o + 14] = hz - dz * len * 0.5; m[o + 15] = 1;
    const col = this.mesh.instanceColor!.array as Float32Array;
    col[i * 3] = r; col[i * 3 + 1] = g; col[i * 3 + 2] = b;
    (this.alpha.array as Float32Array)[i] = a;
  }

  /** dt 초 진행 + 시뮬 칸 기록. 즉시 칸은 이후 begin/push/end */
  update(dt: number): void {
    let i = 0;
    while (i < this.n) {
      if (dt > 0) {
        this.age[i] += dt;
        if (this.age[i] >= this.life[i]) { this.kill(i); continue; }
        this.px[i] += this.vx[i] * dt; this.py[i] += this.vy[i] * dt; this.pz[i] += this.vz[i] * dt;
      }
      const t = this.age[i] / this.life[i];
      this.put(i, this.px[i], this.py[i], this.pz[i], this.dx[i], this.dy[i], this.dz[i], this.len[i] * (1 - 0.35 * t), this.wid[i], this.cr[i], this.cg[i], this.cb[i], this.a0[i] * Math.min(1, t * 8) * (1 - t));
      i++;
    }
    this.im = 0;
    this.finish();
  }

  begin(): void { this.im = 0; }

  /** 즉시 칸 하나(이번 프레임만). 칸이 없으면 무시 */
  push(hx: number, hy: number, hz: number, dx: number, dy: number, dz: number, len: number, wid: number, color: number, alpha: number): void {
    if (this.im >= this.immCap) return;
    hex(color);
    this.put(this.n + this.im, hx, hy, hz, dx, dy, dz, len, wid, _c.r, _c.g, _c.b, alpha);
    this.im++;
  }

  end(): void { this.finish(); }

  private finish(): void {
    this.mesh.count = this.n + this.im;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.mesh.instanceColor!.needsUpdate = true;
    this.alpha.needsUpdate = true;
  }

  clear(): void { this.n = 0; this.im = 0; this.mesh.count = 0; }
  prewarm(on: boolean): void { this.mesh.count = on ? 1 : this.n + this.im; }
  dispose(): void { this.mesh.geometry.dispose(); (this.mesh.material as THREE.Material).dispose(); this.mesh.dispose(); }
}

/** 회전 빌보드 별(투사체 머리): 즉시 모드 전용. 중심이 가장 밝고 네 갈래 빛살이 돈다 */
export class BillboardPool {
  readonly mesh: THREE.InstancedMesh;
  private readonly alpha: THREE.InstancedBufferAttribute;
  im = 0;
  private rx = 1; private ry = 0; private rz = 0; private ux = 0; private uy = 1; private uz = 0; private fx = 0; private fy = 0; private fz = -1;

  constructor(readonly cap: number, renderOrder = 24) {
    const geo = new THREE.PlaneGeometry(1, 1);
    const mat = baseMaterial(true);
    const p = uv().sub(0.5).mul(2);
    const r = length(vec2(p.x, p.y));
    const core = pow(float(1).sub(smoothstep(0, 0.55, r)), 1.7);
    const halo = pow(float(1).sub(smoothstep(0, 1, r)), 2.4).mul(0.55);
    // 네 갈래 빛살: 축에 가까울수록 길게
    const spike = float(1).sub(smoothstep(0, 0.16, p.x.abs().min(p.y.abs()))).mul(float(1).sub(smoothstep(0.25, 1, r)));
    mat.colorNode = vec3(1, 1, 1).mul(float(1).add(core.mul(2.2)));
    mat.opacityNode = clamp(core.add(halo).add(spike.mul(0.8)), 0, 1).mul(attribute('aAlpha', 'float'));
    const { mesh, alpha } = makeInstanced(geo, mat, cap, renderOrder, 'mob-fx-heads');
    this.mesh = mesh; this.alpha = alpha;
  }

  setBasis(rx: number, ry: number, rz: number, ux: number, uy: number, uz: number, fx: number, fy: number, fz: number): void {
    this.rx = rx; this.ry = ry; this.rz = rz; this.ux = ux; this.uy = uy; this.uz = uz; this.fx = fx; this.fy = fy; this.fz = fz;
  }

  begin(): void { this.im = 0; }

  push(x: number, y: number, z: number, size: number, rot: number, color: number, alpha: number): void {
    if (this.im >= this.cap) return;
    hex(color);
    const i = this.im++;
    const c = Math.cos(rot) * size, s = Math.sin(rot) * size;
    const m = this.mesh.instanceMatrix.array as Float32Array;
    const o = i * 16;
    m[o] = this.rx * c + this.ux * s; m[o + 1] = this.ry * c + this.uy * s; m[o + 2] = this.rz * c + this.uz * s; m[o + 3] = 0;
    m[o + 4] = -this.rx * s + this.ux * c; m[o + 5] = -this.ry * s + this.uy * c; m[o + 6] = -this.rz * s + this.uz * c; m[o + 7] = 0;
    m[o + 8] = this.fx; m[o + 9] = this.fy; m[o + 10] = this.fz; m[o + 11] = 0;
    m[o + 12] = x; m[o + 13] = y; m[o + 14] = z; m[o + 15] = 1;
    const col = this.mesh.instanceColor!.array as Float32Array;
    col[i * 3] = _c.r; col[i * 3 + 1] = _c.g; col[i * 3 + 2] = _c.b;
    (this.alpha.array as Float32Array)[i] = alpha;
  }

  end(): void {
    this.mesh.count = this.im;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.mesh.instanceColor!.needsUpdate = true;
    this.alpha.needsUpdate = true;
  }

  clear(): void { this.im = 0; this.mesh.count = 0; }
  prewarm(on: boolean): void { this.mesh.count = on ? 1 : this.im; }
  dispose(): void { this.mesh.geometry.dispose(); (this.mesh.material as THREE.Material).dispose(); this.mesh.dispose(); }
}

/**
 * 보호막 구체(즉시 모드): 가장자리가 밝은(프레넬) 반투명 구에 육각 같은 격자선이 천천히 흐른다. alpha가 보호막 잔량에 비례한다.
 * 카메라 쪽 면만 그려 몹을 가리지 않는다(뒷면 제외, 안쪽은 거의 투명).
 */
export class ShieldPool {
  readonly mesh: THREE.InstancedMesh;
  private readonly alpha: THREE.InstancedBufferAttribute;
  private readonly anim: THREE.InstancedBufferAttribute;
  im = 0;

  constructor(readonly cap: number, renderOrder = 21) {
    const geo = new THREE.SphereGeometry(1, 18, 12);
    const anim = new THREE.InstancedBufferAttribute(new Float32Array(cap), 1);
    anim.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aAnim', anim);
    const mat = baseMaterial(true);
    mat.side = THREE.FrontSide;
    const t = attribute('aAnim', 'float');
    const fres = pow(float(1).sub(abs(normalView.dot(positionViewDirection))), 2.4);
    const U = uv();
    // 격자선: 가로 12줄 · 세로 18줄이 천천히 위로 흐른다
    const gx = float(1).sub(smoothstep(0, 0.1, abs(sin(U.x.mul(Math.PI * 18)))));
    const gy = float(1).sub(smoothstep(0, 0.1, abs(sin(U.y.mul(Math.PI * 9).add(t.mul(0.8))))));
    const grid = clamp(gx.add(gy), 0, 1).mul(0.28);
    const pulse = sin(t.mul(3.2)).mul(0.1).add(0.9);
    mat.colorNode = vec3(1, 1, 1).mul(float(0.8).add(fres.mul(1.4)));
    mat.opacityNode = clamp(fres.mul(0.95).add(grid.mul(float(0.35).add(fres))).add(0.06), 0, 1).mul(pulse).mul(attribute('aAlpha', 'float'));
    const { mesh, alpha } = makeInstanced(geo, mat, cap, renderOrder, 'mob-fx-shield');
    this.mesh = mesh; this.alpha = alpha; this.anim = anim;
  }

  begin(): void { this.im = 0; }

  push(x: number, y: number, z: number, radius: number, color: number, alpha: number, anim: number): void {
    if (this.im >= this.cap) return;
    hex(color);
    const i = this.im++;
    const m = this.mesh.instanceMatrix.array as Float32Array;
    const o = i * 16;
    m[o] = radius; m[o + 1] = 0; m[o + 2] = 0; m[o + 3] = 0;
    m[o + 4] = 0; m[o + 5] = radius; m[o + 6] = 0; m[o + 7] = 0;
    m[o + 8] = 0; m[o + 9] = 0; m[o + 10] = radius; m[o + 11] = 0;
    m[o + 12] = x; m[o + 13] = y; m[o + 14] = z; m[o + 15] = 1;
    const col = this.mesh.instanceColor!.array as Float32Array;
    col[i * 3] = _c.r; col[i * 3 + 1] = _c.g; col[i * 3 + 2] = _c.b;
    (this.alpha.array as Float32Array)[i] = alpha;
    (this.anim.array as Float32Array)[i] = anim;
  }

  end(): void {
    this.mesh.count = this.im;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.mesh.instanceColor!.needsUpdate = true;
    this.alpha.needsUpdate = true;
    this.anim.needsUpdate = true;
  }

  clear(): void { this.im = 0; this.mesh.count = 0; }
  prewarm(on: boolean): void { this.mesh.count = on ? 1 : this.im; }
  dispose(): void { this.mesh.geometry.dispose(); (this.mesh.material as THREE.Material).dispose(); this.mesh.dispose(); }
}

export const DECAL_RUNE = 0;
export const DECAL_CRACK = 1;
/** 돌진 경로 바닥 긁힘(진행 방향으로 긴 흠집 줄, 정상 블렌딩 = 어두운 색도 보인다). 지속 칸이 아니라 긁힘 전용 칸(spawnStreak) */
export const DECAL_SCUFF = 2;
/** 착탄 예정 그림자 점(즉시 칸, 정상 블렌딩의 어두운 부드러운 원) */
export const DECAL_SHADOW = 3;
/** 판이 사라지는 시간(초): 풀어 주면 이 시간 동안 옅어진다 */
const DECAL_FADE = 0.4;

/**
 * 바닥 판: 소환 룬(0, 천천히 도는 이중 고리 + 빛살, 점점 밝아짐)·분열 균열(1, 중심에서 갈라지는 틈, 맥동)·돌진 긁힘(2)·그림자 점(3).
 * 지속 칸(persistent)은 spawn/release로 수명을 갖고, 긁힘 칸(streak)은 spawnStreak으로 짧게 떠서 스스로 사라지며, 즉시 칸(immediate)은 매 프레임 push한다(정예 몹에 붙은 균열·투사체 그림자).
 * 판은 바닥 4.5cm 위에 눕는다(깊이 검사 켬, 벽 뒤에 가려진다).
 * 블렌딩은 premultiplied(One, 1−srcA)로 한 메시에서 룬·균열은 가산(알파 0 출력), 긁힘·그림자는 정상(알파 출력)으로 섞는다 — 드로우콜은 그대로 1.
 */
export class DecalPool {
  readonly mesh: THREE.InstancedMesh;
  private readonly alpha: THREE.InstancedBufferAttribute;
  private readonly param: THREE.InstancedBufferAttribute;
  im = 0;
  active = 0;
  /** 지속 + 긁힘 칸 수 */
  private readonly slots: number;
  private readonly used: Uint8Array;
  private readonly kind: Uint8Array;
  private readonly x: Float32Array; private readonly z: Float32Array; private readonly rad: Float32Array;
  private readonly age: Float32Array; private readonly life: Float32Array;
  private readonly rise: Float32Array; private readonly rel: Float32Array; private readonly seed: Float32Array;
  private readonly cr: Float32Array; private readonly cg: Float32Array; private readonly cb: Float32Array;
  /** 긁힘: 진행 방향 단위 벡터와 반폭(m), 시작 밝기 */
  private readonly dx: Float32Array; private readonly dz: Float32Array; private readonly wid: Float32Array; private readonly a0: Float32Array;
  /** 풀어 준 뒤 지난 시간(초), −1 = 아직 */
  private readonly relAge: Float32Array;

  constructor(readonly persistentCap: number, readonly immediateCap: number, renderOrder = 7, readonly streakCap = 0) {
    const slots = this.slots = persistentCap + streakCap;
    const cap = slots + immediateCap;
    const geo = new THREE.PlaneGeometry(2, 2);
    geo.rotateX(-Math.PI / 2);
    const param = new THREE.InstancedBufferAttribute(new Float32Array(cap * 4), 4);
    param.setUsage(THREE.DynamicDrawUsage);
    geo.setAttribute('aP', param);
    const mat = baseMaterial(true);
    mat.depthWrite = false;
    const aP = attribute('aP', 'vec4');
    const kind = aP.x, bright = aP.y, rot = aP.z, seed = aP.w;
    const p = uv().sub(0.5).mul(2);
    const r = length(vec2(p.x, p.y));
    const ang = atan(p.y, p.x);
    const edge = float(1).sub(smoothstep(0.9, 1.0, r));
    // 룬: 바깥 고리 · 끊어진 중간 고리 · 8갈래 빛살 · 안쪽 고리 · 중심 빛
    const outer = lineMask(r.sub(0.93), 0.05);
    const dashed = lineMask(r.sub(0.74), 0.04).mul(step(-0.25, sin(ang.mul(16).add(rot.mul(2)))));
    const band = smoothstep(0.3, 0.38, r).mul(float(1).sub(smoothstep(0.62, 0.7, r)));
    const spokes = pow(abs(sin(ang.mul(4).sub(rot))), 14).mul(band);
    const inner = lineMask(r.sub(0.28), 0.035);
    const glow = pow(float(1).sub(smoothstep(0, 0.55, r)), 2);
    const runeA = clamp(outer.add(dashed.mul(0.9)).add(spokes.mul(0.85)).add(inner).add(glow.mul(0.55)), 0, 1).mul(edge);
    // 균열: 중심에서 뻗는 5~6줄 갈라진 선(반경에 따라 휘어짐) + 중심 빛
    const jag = ang.mul(5).add(sin(r.mul(11).add(seed.mul(6.283))).mul(1.1)).add(sin(r.mul(27).add(seed.mul(3))).mul(0.35));
    const ridge = lineMask(sin(jag), 0.16).mul(smoothstep(1, 0.2, r));
    const crackA = clamp(ridge.add(pow(float(1).sub(smoothstep(0, 0.42, r)), 2).mul(0.8)), 0, 1).mul(edge);
    // 긁힘(가로로 긴 판, x = 진행 방향): 끊어진 흠집 세 줄 + 옅은 먼지 번짐, 양 끝으로 갈수록 가늘고 옅어진다
    const along = abs(p.x);
    const ends = float(1).sub(smoothstep(0.55, 1.0, along));
    const lw = float(0.07).add(float(0.07).mul(float(1).sub(along)));
    const yOff = seed.sub(0.5).mul(0.18);
    const sc = (off: number, ph: number): Node<'float'> => float(1).sub(smoothstep(0, 1, abs(p.y.sub(yOff).sub(off)).div(lw))).mul(step(-0.3, sin(p.x.mul(8).add(seed.mul(21)).add(ph))));
    const scratches = clamp(sc(-0.5, 0).add(sc(0.02, 2.1)).add(sc(0.5, 4.3)), 0, 1);
    const smear = float(1).sub(smoothstep(0, 0.95, abs(p.y))).mul(0.4);
    const scuffA = clamp(scratches.mul(0.95).add(smear), 0, 1).mul(ends);
    // 그림자 점: 가운데가 가장 짙은 부드러운 원
    const shadowA = pow(float(1).sub(smoothstep(0.1, 1.0, r)), 1.3);
    const isExt = step(1.5, kind);
    const base = mix(runeA, crackA, step(0.5, kind));
    const ext = mix(scuffA, shadowA, step(2.5, kind));
    mat.colorNode = vec3(1, 1, 1).mul(float(1).add(glow.mul(mix(float(0.8), float(1.6), step(0.5, kind))).mul(float(1).sub(isExt))));
    mat.opacityNode = mix(base, ext, isExt).mul(bright);
    // premultiplied 합성: 룬·균열 = rgb·a, 알파 0(가산과 같다) / 긁힘·그림자 = rgb·a, 알파 a(정상 블렌딩)
    mat.blending = THREE.CustomBlending;
    mat.blendEquation = THREE.AddEquation;
    mat.blendSrc = THREE.OneFactor;
    mat.blendDst = THREE.OneMinusSrcAlphaFactor;
    mat.outputNode = vec4(output.rgb.mul(output.a), output.a.mul(isExt));
    const alpha = new THREE.InstancedBufferAttribute(new Float32Array(cap), 1);
    geo.setAttribute('aAlpha', alpha);
    const mesh = new THREE.InstancedMesh(geo, mat, cap);
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    mesh.setColorAt(0, new THREE.Color(1, 1, 1));
    (mesh.instanceColor as THREE.InstancedBufferAttribute).setUsage(THREE.DynamicDrawUsage);
    mesh.frustumCulled = false;
    mesh.count = 0;
    mesh.renderOrder = renderOrder;
    mesh.name = 'mob-fx-decal';
    this.mesh = mesh; this.alpha = alpha; this.param = param;
    const n = slots;
    this.used = new Uint8Array(n); this.kind = new Uint8Array(n);
    const f = (): Float32Array => new Float32Array(n);
    this.x = f(); this.z = f(); this.rad = f(); this.age = f(); this.life = f(); this.rise = f(); this.rel = f(); this.seed = f();
    this.cr = f(); this.cg = f(); this.cb = f(); this.relAge = f().fill(-1);
    this.dx = f(); this.dz = f(); this.wid = f(); this.a0 = f();
    // 사용하지 않는 칸은 크기 0으로 둔다
    const m = mesh.instanceMatrix.array as Float32Array;
    for (let i = 0; i < cap; i++) { const o = i * 16; m[o + 15] = 1; }
  }

  /**
   * 지속 칸 하나를 연다. life = 유지 시간(초, Infinity면 release까지), rise = 밝아지는 데 걸리는 시간(초). 가득 차면 가장 오래된 칸을 대체한다.
   * 칸 번호를 돌려준다(release에 쓴다)
   */
  spawn(kind: number, x: number, z: number, radius: number, color: number, life: number, rise: number, seed = 0): number {
    let slot = -1;
    for (let i = 0; i < this.persistentCap; i++) if (!this.used[i]) { slot = i; break; }
    if (slot < 0) {
      let best = -1;
      for (let i = 0; i < this.persistentCap; i++) {
        const f = this.life[i] === Infinity ? this.age[i] / 8 : this.age[i] / this.life[i];
        if (f > best) { best = f; slot = i; }
      }
      this.active--;
    }
    this.used[slot] = 1; this.kind[slot] = kind; this.x[slot] = x; this.z[slot] = z; this.rad[slot] = radius;
    this.age[slot] = 0; this.life[slot] = life; this.rise[slot] = Math.max(0.01, rise); this.seed[slot] = seed; this.relAge[slot] = -1;
    hex(color); this.cr[slot] = _c.r; this.cg[slot] = _c.g; this.cb[slot] = _c.b;
    this.active++;
    return slot;
  }

  /** 칸을 풀어 준다: 이후 DECAL_FADE초 동안 옅어지고 사라진다 */
  release(slot: number): void {
    if (slot >= 0 && slot < this.persistentCap && this.used[slot] && this.relAge[slot] < 0) this.relAge[slot] = 0;
  }

  isUsed(slot: number): boolean { return slot >= 0 && slot < this.persistentCap && this.used[slot] === 1; }

  /**
   * 긁힘 칸 하나를 연다(돌진 경로 바닥 자국): (x,z)가 중심, (dx,dz) 진행 방향 단위 벡터, halfLen·halfWid = 반길이·반폭(m), life초 동안 옅어지며 사라진다.
   * 긁힘 칸이 가득 차면 가장 오래된 것을 대체한다. 칸이 없으면(streakCap 0) 무시
   */
  spawnStreak(x: number, z: number, dx: number, dz: number, halfLen: number, halfWid: number, color: number, alpha: number, life: number, seed: number): void {
    if (this.streakCap <= 0) return;
    let slot = -1, best = -1;
    for (let i = this.persistentCap; i < this.slots; i++) {
      if (!this.used[i]) { slot = i; best = 2; break; }
      const f = this.age[i] / this.life[i];
      if (f > best) { best = f; slot = i; }
    }
    if (this.used[slot]) this.active--;
    this.used[slot] = 1; this.kind[slot] = DECAL_SCUFF; this.x[slot] = x; this.z[slot] = z; this.rad[slot] = halfLen; this.wid[slot] = halfWid;
    this.dx[slot] = dx; this.dz[slot] = dz; this.a0[slot] = alpha; this.age[slot] = 0; this.life[slot] = life; this.seed[slot] = seed; this.relAge[slot] = -1;
    hex(color); this.cr[slot] = _c.r; this.cg[slot] = _c.g; this.cb[slot] = _c.b;
    this.active++;
  }

  /** dt 초 진행하고 지속·긁힘 칸을 기록한다. 즉시 칸은 이후 begin/push/end */
  update(dt: number): void {
    const m = this.mesh.instanceMatrix.array as Float32Array;
    const col = this.mesh.instanceColor!.array as Float32Array;
    const pa = this.param.array as Float32Array;
    const al = this.alpha.array as Float32Array;
    for (let i = 0; i < this.slots; i++) {
      const o = i * 16;
      const streak = i >= this.persistentCap;
      if (!this.used[i]) { m[o] = m[o + 2] = m[o + 5] = m[o + 8] = m[o + 10] = 0; pa[i * 4 + 1] = 0; continue; }
      if (dt > 0) {
        this.age[i] += dt;
        if (this.relAge[i] >= 0) this.relAge[i] += dt;
      }
      if (streak) {
        const t = this.age[i] / this.life[i];
        if (t >= 1) { this.used[i] = 0; this.active--; m[o] = m[o + 2] = m[o + 5] = m[o + 8] = m[o + 10] = 0; pa[i * 4 + 1] = 0; continue; }
        // 긴 축 = 진행 방향, 짧은 축 = 수직(바닥 위 평면이라 위쪽 축 크기는 무관)
        const L = this.rad[i], Wd = this.wid[i];
        m[o] = this.dx[i] * L; m[o + 1] = 0; m[o + 2] = this.dz[i] * L; m[o + 3] = 0;
        m[o + 4] = 0; m[o + 5] = 1; m[o + 6] = 0; m[o + 7] = 0;
        m[o + 8] = -this.dz[i] * Wd; m[o + 9] = 0; m[o + 10] = this.dx[i] * Wd; m[o + 11] = 0;
        m[o + 12] = this.x[i]; m[o + 13] = 0.04; m[o + 14] = this.z[i]; m[o + 15] = 1;
        col[i * 3] = this.cr[i]; col[i * 3 + 1] = this.cg[i]; col[i * 3 + 2] = this.cb[i];
        // 생기자마자 선명하고(0.04초에 가득) 뒤로 갈수록 빠르게 옅어진다
        pa[i * 4] = DECAL_SCUFF; pa[i * 4 + 1] = this.a0[i] * Math.min(1, this.age[i] / 0.04) * Math.pow(1 - t, 1.4);
        pa[i * 4 + 2] = 0; pa[i * 4 + 3] = this.seed[i];
        al[i] = 1;
        continue;
      }
      if (this.age[i] >= this.life[i] && this.relAge[i] < 0) this.relAge[i] = 0;
      if (this.relAge[i] >= DECAL_FADE) { this.used[i] = 0; this.active--; m[o] = m[o + 5] = m[o + 10] = 0; pa[i * 4 + 1] = 0; continue; }
      const up = Math.min(1, this.age[i] / this.rise[i]);
      const fade = this.relAge[i] < 0 ? 1 : 1 - this.relAge[i] / DECAL_FADE;
      // 룬은 밝아질수록 커지며 자리를 잡는다(0.8 → 1). 풀린 뒤에는 한 번 번쩍(조금 크게)하며 사라진다
      const k = this.kind[i] === DECAL_RUNE ? 0.8 + 0.2 * up + (this.relAge[i] >= 0 ? 0.12 * (1 - fade) : 0) : 0.85 + 0.15 * up;
      const R = this.rad[i] * k;
      m[o] = R; m[o + 5] = R; m[o + 10] = R; m[o + 15] = 1;
      m[o + 12] = this.x[i]; m[o + 13] = 0.045; m[o + 14] = this.z[i];
      col[i * 3] = this.cr[i]; col[i * 3 + 1] = this.cg[i]; col[i * 3 + 2] = this.cb[i];
      const pulse = this.kind[i] === DECAL_CRACK ? 0.75 + 0.25 * Math.sin(this.age[i] * 14) : 1;
      pa[i * 4] = this.kind[i]; pa[i * 4 + 1] = (0.25 + 0.75 * up * up) * fade * pulse;
      pa[i * 4 + 2] = this.age[i] * 0.9; pa[i * 4 + 3] = this.seed[i];
      al[i] = 1;
    }
    this.im = 0;
    this.finish();
  }

  begin(): void { this.im = 0; }

  /** 즉시 칸(균열·그림자 점): 정예 몹 발밑·착탄 지점에 매 프레임. 크기 균일(radius) */
  push(kind: number, x: number, z: number, radius: number, color: number, bright: number, rot: number, seed: number): void {
    if (this.im >= this.immediateCap) return;
    hex(color);
    const i = this.slots + this.im++;
    const m = this.mesh.instanceMatrix.array as Float32Array;
    const o = i * 16;
    m[o] = radius; m[o + 1] = 0; m[o + 2] = 0; m[o + 3] = 0;
    m[o + 4] = 0; m[o + 5] = radius; m[o + 6] = 0; m[o + 7] = 0;
    m[o + 8] = 0; m[o + 9] = 0; m[o + 10] = radius; m[o + 11] = 0;
    m[o + 12] = x; m[o + 13] = 0.05; m[o + 14] = z; m[o + 15] = 1;
    const col = this.mesh.instanceColor!.array as Float32Array;
    col[i * 3] = _c.r; col[i * 3 + 1] = _c.g; col[i * 3 + 2] = _c.b;
    const pa = this.param.array as Float32Array;
    pa[i * 4] = kind; pa[i * 4 + 1] = bright; pa[i * 4 + 2] = rot; pa[i * 4 + 3] = seed;
    (this.alpha.array as Float32Array)[i] = 1;
  }

  end(): void { this.finish(); }

  private finish(): void {
    this.mesh.count = this.active > 0 || this.im > 0 ? this.slots + this.im : 0;
    this.mesh.instanceMatrix.needsUpdate = true;
    this.mesh.instanceColor!.needsUpdate = true;
    this.param.needsUpdate = true;
    this.alpha.needsUpdate = true;
  }

  clear(): void {
    this.used.fill(0); this.active = 0; this.im = 0; this.mesh.count = 0;
  }
  prewarm(on: boolean): void { this.mesh.count = on ? this.slots : this.active > 0 || this.im > 0 ? this.slots + this.im : 0; }
  dispose(): void { this.mesh.geometry.dispose(); (this.mesh.material as THREE.Material).dispose(); this.mesh.dispose(); }
}
